import type { Category } from '@wix/answers-api';
import { Header, PageType as CommonPageType } from '@wix/cc-user-facing-common-components';
import { useRouter } from 'next/router';
import React, { useCallback, useContext, useEffect, useState } from 'react';

import { useHeaderBI } from './bi';
import { Context } from '../../context';
import { useGoToSearchResultsPage } from '../../hooks/useGoToSearchResultsPage';
import { useIsGenAnswer } from '../../hooks/useIsGenAnswer';
import useIsHelpCenterCopilotEnabled from '../../hooks/useIsHelpCenterCopilotEnabled';
import { useIsSitePickerEnabled } from '../../hooks/useIsSitePickerEnabled';
import { PageType } from '../../types';
import { useStore } from '../../zustand/stores';
import { InnerLink } from '../InnerLink';
import { SiteSelector, SiteSelectorParent } from '../SiteSelector';

const SiteSelectorWrapper: React.FC<{
  itemId?: string;
  parent: string;
  parentElm: HTMLElement | null;
  relativeText: string;
}> = ({ itemId, parent, parentElm, relativeText }) => (
  <SiteSelector
    itemId={itemId}
    parent={parent as SiteSelectorParent}
    parentElm={parentElm}
    relativeText={relativeText}
  />
);

const HeaderWrapper: React.FC = () => {
  const categoriesTree = useStore(
    (state) => state.categoriesTreeSlice.categoriesTree
  );
  const { isWixStudioUser, user } = useStore((state) => state.userSlice);
  const context = useContext(Context);
  const {
    pageType,
    answersApi,
    showSearchModal,
    setShowSearchModal,
    siteSelectorData,
    pageTemplateData: { itemId },
  } = context;
  const { locale, asPath } = useRouter();
  const [fallbackCategoriesTree, setFallbackCategoriesTree] = useState<Category[]>([]);
  const isHelpCenterCopilotEnabled = useIsHelpCenterCopilotEnabled();
  const isGenAnswerEnabled = useIsGenAnswer();
  const isSitePickerEnabled = useIsSitePickerEnabled();
  const { goToSearchResultsPage } = useGoToSearchResultsPage();
  const { onLinkClick, onItemView } = useHeaderBI();

  useEffect(() => {
    const fetchCategories = async () => {
      if (!categoriesTree || categoriesTree.length === 0) {
        try {
          const categories = await answersApi.kb.getTree({
            locale: locale || 'en',
          });
          setFallbackCategoriesTree(categories);
        } catch (error) {
          console.error('Failed to fetch categories tree:', error);
          setFallbackCategoriesTree([]);
        }
      }
    };

    void fetchCategories();
  }, [categoriesTree, answersApi, locale]);

  const effectiveCategoriesTree =
    categoriesTree && categoriesTree.length > 0
      ? categoriesTree
      : fallbackCategoriesTree;

  const handleGoToSearchResultsPage = useCallback(
    async (params: { term: string; previousPageType: CommonPageType; overrideTerm?: string }) => {
      await goToSearchResultsPage({
        term: params.term,
        previousPageType: params.previousPageType as unknown as PageType,
        overrideTerm: params.overrideTerm,
      });
    },
    [goToSearchResultsPage]
  );

  return (
    <Header
      locale={locale || 'en'}
      user={user}
      isWixStudioUser={isWixStudioUser}
      pageType={pageType}
      categoriesTree={effectiveCategoriesTree}
      isHelpCenterCopilotEnabled={isHelpCenterCopilotEnabled}
      isGenAnswerEnabled={isGenAnswerEnabled}
      isSitePickerEnabled={isSitePickerEnabled}
      siteSelectorData={siteSelectorData}
      itemId={itemId}
      showSearchModal={showSearchModal}
      setShowSearchModal={setShowSearchModal}
      biCallbacks={{ onLinkClick, onItemView }}
      goToSearchResultsPage={handleGoToSearchResultsPage}
      ssrDeviceHint={{ isMobile: context.isMobile, isTablet: context.isTablet }}
      currentPath={asPath}
      InnerLinkComponent={InnerLink}
      SiteSelectorComponent={SiteSelectorWrapper}
    />
  );
};

export default HeaderWrapper;

